import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, Clock, ClipboardCheck, FileText, Heart, Home, MailCheck, XCircle } from 'lucide-react';

const getApplicationStatus = (application) => application?.decision_status || application?.status || 'pending';

const getStatusBadge = (status) => {
  if (status === 'approved') {
    return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
  }
  if (status === 'rejected') {
    return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
};

const getStatusIcon = (status) => {
  if (status === 'approved') return <CheckCircle className="h-4 w-4 text-green-600" />;
  if (status === 'rejected') return <XCircle className="h-4 w-4 text-red-600" />;
  return <Clock className="h-4 w-4 text-amber-600" />;
};

const getPropertyTitle = (item) => (
  item?.property_title
  || item?.property?.title
  || item?.title
  || item?.property?.address
  || 'Property'
);

export const PreTenancyDashboard = ({
  user,
  notifications = [],
  savedProperties = [],
  applications = [],
  inviteStatus = null,
  onUnsaveSavedProperty,
  onWithdrawApplication,
  onMarkNotificationAsRead
}) => {
  const pendingInviteNotifications = (notifications || []).filter((notification) => {
    const type = notification.notification_type || notification.type || '';
    const text = `${notification.title || ''} ${notification.message || ''}`.toLowerCase();
    return type.includes('invite') || text.includes('invited');
  });

  return (
    <div className="bg-navy-50 min-h-screen" data-testid="tenant-pre-dashboard">
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-navy-900">
                Welcome back, {user?.name}
              </h1>
              <p className="text-muted-foreground">
                Track applications, saved homes, and tenancy invites from one place.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-6">
        {inviteStatus && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <MailCheck className="mt-0.5 h-5 w-5 text-green-700" />
                <div>
                  <h2 className="font-heading text-xl font-semibold text-green-950">
                    You have successfully joined {inviteStatus.propertyTitle || 'your property'}
                  </h2>
                  <p className="mt-1 text-sm text-green-800">
                    Your tenancy is active. Opening My Tenancy will take you to your dashboard.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {pendingInviteNotifications.length > 0 && !inviteStatus && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <MailCheck className="mt-0.5 h-5 w-5 text-blue-700" />
                <div>
                  <h2 className="font-heading text-xl font-semibold text-blue-950">
                    You've been invited — complete registration to access your tenancy
                  </h2>
                  <p className="mt-1 text-sm text-blue-800">
                    Use the invite link from your email. Once accepted, this dashboard will switch to your active tenancy.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-navy-900">Applications</h2>
                <p className="text-sm text-muted-foreground">You are not an active tenant yet. These are your current application statuses.</p>
              </div>
              <Button asChild variant="outline">
                <Link to="/tenant/explore#browse-section">
                  <Home className="h-4 w-4 mr-2" />
                  Browse listings
                </Link>
              </Button>
            </div>

            {applications.length > 0 ? (
              <div className="space-y-3">
                {applications.map((application) => {
                  const status = getApplicationStatus(application);
                  return (
                    <div key={application.id} className="rounded-md border p-4 bg-white">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {getStatusIcon(status)}
                            <Link
                              to={`/tenant/explore?applicationId=${application.id}#applications`}
                              className="font-medium text-navy-900 hover:underline"
                            >
                              {getPropertyTitle(application)}
                            </Link>
                            {getStatusBadge(status)}
                          </div>
                          <p className="mt-2 text-sm text-muted-foreground">
                            {status === 'approved'
                              ? 'Application approved — waiting for landlord to assign or invite you.'
                              : status === 'rejected'
                                ? 'This application was not taken forward.'
                                : application.screening_status === 'sent'
                                  ? 'Action needed: complete the pre-screening questionnaire sent by the landlord.'
                                  : application.screening_status === 'completed'
                                    ? 'Screening submitted — waiting for the landlord to review.'
                                    : 'Application pending — the landlord has not made a decision yet.'}
                          </p>
                          {application.screening_status === 'sent' && (
                            <div className="mt-3">
                              <Button asChild size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
                                <Link to={`/tenant/explore?applicationId=${application.id}#applications`}>
                                  <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />
                                  Complete Questionnaire
                                </Link>
                              </Button>
                            </div>
                          )}
                        </div>
                        {status === 'pending' && !application.screening_status && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => onWithdrawApplication?.(application)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-6 text-center">
                <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="font-medium text-navy-900">No applications yet</p>
                <p className="text-sm text-muted-foreground mt-1">Apply for a listing and your status will appear here.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <h2 className="font-heading text-2xl font-semibold text-navy-900">Saved properties</h2>
              <p className="text-sm text-muted-foreground">Keep track of homes you may want to apply for.</p>
            </div>

            {savedProperties.length > 0 ? (
              <div className="space-y-3">
                {savedProperties.map((saved) => {
                  const property = saved.property || saved;
                  const propertyId = saved.property_id || property.id;
                  const externalUrl = property.external_url || saved.external_url;
                  const detailHref = externalUrl || (propertyId ? `/properties/${propertyId}` : '/tenant/explore');
                  return (
                    <div key={saved.id || propertyId || externalUrl} className="rounded-md border p-4 bg-white">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Heart className="h-4 w-4 text-red-500" />
                            <p className="font-medium truncate">{getPropertyTitle(saved)}</p>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{property.address || property.location || 'Saved property'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button asChild size="sm" variant="outline">
                            <a href={detailHref} target={externalUrl ? '_blank' : undefined} rel={externalUrl ? 'noreferrer' : undefined}>
                              View
                            </a>
                          </Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => onUnsaveSavedProperty?.(saved)}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-6 text-center">
                <Heart className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="font-medium text-navy-900">No saved properties yet</p>
                <p className="text-sm text-muted-foreground mt-1">Save listings while browsing and they will appear here.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
